/**
 * Device Token Manager
 * Handles device token storage with redundancy (localStorage + cookie)
 */

const DEVICE_TOKEN_KEY = 'device_token';
const DEVICE_TOKEN_COOKIE = 'dt_backup';

/**
 * Generate a unique device token
 */
const generateDeviceToken = (): string => {
    // Use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    // Fallback: Generate a UUID v4-like string
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

/**
 * Save device token to both localStorage and cookie
 */
export const saveDeviceToken = (token: string): void => {
    try {
        // Save to localStorage
        localStorage.setItem(DEVICE_TOKEN_KEY, token);

        // Save to cookie as backup (expires in 365 days)
        const expires = new Date();
        expires.setDate(expires.getDate() + 365);
        document.cookie = `${DEVICE_TOKEN_COOKIE}=${token}; expires=${expires.toUTCString()}; path=/; SameSite=Strict`;

        console.log('✅ Device token saved to localStorage and cookie');
    } catch (error) {
        console.error('Error saving device token:', error);
    }
};

/**
 * Get device token from localStorage or cookie (fallback)
 * If no token exists, generate a new one
 */
export const getDeviceToken = (): string | null => {
    try {
        // Try localStorage first
        let token = localStorage.getItem(DEVICE_TOKEN_KEY);

        if (!token) {
            // Fallback to cookie
            token = getCookie(DEVICE_TOKEN_COOKIE);

            if (token) {
                // Restore to localStorage from cookie
                localStorage.setItem(DEVICE_TOKEN_KEY, token);
                console.log('🔄 Device token restored from cookie to localStorage');
            } else {
                // Generate new token if none exists
                token = generateDeviceToken();
                saveDeviceToken(token);
                console.log('🆕 New device token generated and saved');
            }
        }

        return token;
    } catch (error) {
        console.error('Error getting device token:', error);
        return null;
    }
};

/**
 * Remove device token from both localStorage and cookie
 */
export const removeDeviceToken = (): void => {
    try {
        localStorage.removeItem(DEVICE_TOKEN_KEY);
        document.cookie = `${DEVICE_TOKEN_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        console.log('🗑️ Device token removed from localStorage and cookie');
    } catch (error) {
        console.error('Error removing device token:', error);
    }
};

/**
 * Ensure device token exists in localStorage (restore from cookie or generate if needed)
 */
export const ensureDeviceToken = (): boolean => {
    const token = getDeviceToken(); // This will now auto-generate if needed
    return !!token;
};

/**
 * Helper function to get cookie value
 */
function getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) {
        const cookieValue = parts.pop()?.split(';').shift();
        return cookieValue || null;
    }

    return null;
}
